import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Camera, ArrowLeft, Star, CheckCircle, Clock, Briefcase, Image } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import ContactModal from '../components/ContactModal';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const resolveName = (nameObj, lang) =>
  nameObj?.[lang] ?? nameObj?.['es'] ?? nameObj?.['en'] ?? '';

const TierBadge = ({ tier }) => {
  const { t } = useTranslation();
  const colors = {
    1: 'bg-gray-100 text-gray-700',
    2: 'bg-blue-100 text-blue-700',
    3: 'bg-purple-100 text-purple-700',
    4: 'bg-amber-100 text-amber-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[tier] || colors[1]}`}>
      {t(`provider.tier_${tier || 1}`)}
    </span>
  );
};

const StarRow = ({ rating, count }) => (
  <div className="flex items-center gap-1.5">
    {[1, 2, 3, 4, 5].map(n => (
      <Star
        key={n}
        className={`w-4 h-4 ${n <= Math.round(rating) ? 'fill-yellow-400 stroke-yellow-400' : 'stroke-gray-300'}`}
      />
    ))}
    <span className="text-sm text-muted-foreground ml-1">
      {rating ? rating.toFixed(1) : '—'} {count != null && `(${count})`}
    </span>
  </div>
);

const ProviderProfilePage = () => {
  const { providerId } = useParams();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [provider, setProvider] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    bio: { es: '', en: '', fr: '' },
    hourly_rate: 0,
    is_available: true,
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  // Verification state
  const [verifStatus, setVerifStatus] = useState(null);

  // If no providerId in URL, it's the provider viewing their own profile
  const targetId = providerId || user?.id;
  const isOwnProfile = user?.id === targetId && user?.role === 'provider';

  const fetchProvider = useCallback(async () => {
    if (!targetId) return;
    try {
      const res = await axios.get(`${API}/providers/${targetId}/profile`);
      setProvider(res.data);
      const pp = res.data.provider_profile || {};
      setProfileData({
        bio: typeof pp.bio === 'object' ? pp.bio : { es: pp.bio || '', en: '', fr: '' },
        hourly_rate: pp.hourly_rate || 0,
        is_available: pp.is_available !== false,
      });
    } catch {
      toast.error('Failed to load provider');
    } finally {
      setLoading(false);
    }
  }, [targetId]);

  const fetchVerifStatus = useCallback(async () => {
    if (!isOwnProfile) return;
    try {
      const res = await axios.get(`${API}/providers/verification/status`);
      setVerifStatus(res.data);
    } catch {
      // non-fatal
    }
  }, [isOwnProfile]);

useEffect(() => {
    fetchProvider();
    fetchVerifStatus();
  }, [fetchProvider, fetchVerifStatus]);

  const handleSaveProfile = async () => {
    try {
      await axios.put(`${API}/provider/profile`, profileData);
      toast.success('Profile updated!');
      setIsEditing(false);
      fetchProvider();
    } catch {
      toast.error('Failed to update profile');
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles.length) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', acceptedFiles[0]);
      const res = await axios.post(`${API}/upload-profile-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Profile image updated!');
      const updatedUser = { ...user, profile_photo: res.data.image_url };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      window.location.reload();
    } catch {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  }, [user]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    maxSize: 5242880,
    multiple: false,
  });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!provider) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Provider not found.</p>
      </div>
    );
  }

  const pp = provider.provider_profile || {};
  const reviews = provider.reviews || [];
  const offeredServices = provider.offered_services || [];
  const portfolio = pp.portfolio || [];

  return (
    <>
    <div className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
        {/* ── Header card ── */}
        <Card data-testid="provider-profile">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center text-white text-3xl font-bold overflow-hidden">
                  {provider.profile_photo ? (
                    <img
                      src={provider.profile_photo.startsWith('http') ? provider.profile_photo : `${process.env.REACT_APP_BACKEND_URL || ''}${provider.profile_photo}`}
                      alt={provider.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    provider.name?.charAt(0).toUpperCase()
                  )}
                </div>
                {isOwnProfile && (
                  <div
                    {...getRootProps()}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer border"
                    title={uploadingImage ? 'Uploading...' : 'Change photo'}
                  >
                    <input {...getInputProps()} />
                    <Camera className="w-4 h-4" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>{provider.name}</h2>
                  <TierBadge tier={pp.verification_tier} />
                  {pp.is_available !== false ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {t('provider.available_now')}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      {t('provider.unavailable')}
                    </Badge>
                  )}
                </div>

                {/* Provider type */}
                {pp.provider_type && pp.provider_type !== 'individual' && (
                  <p className="text-sm text-muted-foreground mb-1">
                    {t(`provider.${pp.provider_type}`) || pp.provider_type}
                    {pp.business_name && ` · ${pp.business_name}`}
                  </p>
                )}

                <StarRow rating={pp.rating} count={reviews.length} />

                {/* Stats row */}
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                  {pp.completed_jobs != null && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-3.5 h-3.5" />
                      {pp.completed_jobs} {t('provider.completed_jobs')}
                    </span>
                  )}
                  {pp.response_time_hrs != null && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {pp.response_time_hrs}h {t('provider.response_time')}
                    </span>
                  )}
                </div>

                {/* Edit bio or show bio */}
                {isEditing ? (
                  <div className="mt-4 space-y-4">
                    <div>
                      <Label>{t('provider.bio')}</Label>
                      <Textarea
                        value={profileData.bio?.es || ''}
                        onChange={e => setProfileData(prev => ({
                          ...prev,
                          bio: { es: e.target.value, en: e.target.value, fr: e.target.value },
                        }))}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>{t('provider.hourly_rate')} (USD)</Label>
                      <Input
                        type="number"
                        value={profileData.hourly_rate}
                        onChange={e => setProfileData(prev => ({ ...prev, hourly_rate: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_available"
                        checked={profileData.is_available}
                        onChange={e => setProfileData(prev => ({ ...prev, is_available: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="is_available">{t('provider.available_now')}</Label>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                      <Button onClick={handleSaveProfile}>{t('provider.save')}</Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3">
                    <p className="text-sm text-muted-foreground">
                      {resolveName(pp.bio, i18n.language) || <span className="italic">No bio yet.</span>}
                    </p>
                    {isOwnProfile && (
                      <Button className="mt-3" size="sm" variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-profile-button">
                        Edit Profile
                      </Button>
                    )}
                    {!isOwnProfile && user?.role === 'client' && (
                      <Button
                        className="mt-3"
                        style={{ background: 'hsl(48 100% 50%)', color: 'hsl(215 25% 12%)' }}
                        onClick={() => setContactOpen(true)}
                        data-testid="contact-provider-button"
                      >
                        <Briefcase className="w-4 h-4 mr-2" />
                        {t('provider.contact_button')}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Verification section (own profile only) ── */}
        {isOwnProfile && verifStatus && (
          <Card>
            <CardContent className="flex items-center justify-between pt-5">
              <div className="space-y-1">
                <p className="text-sm font-medium">{t('verification.title')}</p>
                {verifStatus.verification_status === 'approved' && (
                  <p className="text-xs text-green-600">{t('verification.approved')} — {t(`provider.tier_${verifStatus.verification_tier || 1}`)}</p>
                )}
                {verifStatus.verification_status === 'pending_review' && (
                  <p className="text-xs text-amber-600">{t('verification.pending')}</p>
                )}
                {verifStatus.verification_status === 'rejected' && (
                  <p className="text-xs text-red-600">{t('verification.rejected')}</p>
                )}
                {(!verifStatus.verification_status || verifStatus.verification_status === 'not_submitted') && (
                  <p className="text-xs text-muted-foreground">{t('verification.unverified')}</p>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate('/provider/verification')}>
                {t('verification.submit_title')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Services ── */}
        {offeredServices.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('provider.services')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {offeredServices.map(svc => (
                <div key={svc.id} className="flex justify-between items-start p-3 border rounded-lg">
                  <div className="min-w-0">
                    {svc.subcategory_name && (
                      <p className="text-xs text-muted-foreground mb-0.5">
                        {resolveName(svc.subcategory_name, i18n.language)}
                      </p>
                    )}
                    <p className="font-medium text-sm">
                      {resolveName(svc.service_name, i18n.language)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {resolveName(svc.description, i18n.language)}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <p className="font-bold text-green-700">${svc.rate}</p>
                    <p className="text-xs text-muted-foreground">/ {svc.unit}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* ── Portfolio ── */}
        {portfolio.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5" />
                {t('provider.portfolio')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {portfolio.map((url, i) => (
                  <div key={i} className="aspect-square rounded-lg overflow-hidden border bg-gray-50">
                    <img src={url} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Reviews ── */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5" />
              Reviews
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reviews.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('provider.no_reviews')}</p>
            ) : (
              <div className="space-y-4">
                {reviews.map(r => (
                  <div key={r.id} className="border-b last:border-0 pb-4 last:pb-0">
                    <StarRow rating={r.rating} />
                    {r.comment && <p className="text-sm mt-1">{r.comment}</p>}
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(r.created_at).toLocaleDateString(i18n.language)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Contact modal */}
      {!isOwnProfile && user?.role === 'client' && (
        <ContactModal
          open={contactOpen}
          onClose={() => setContactOpen(false)}
          providerId={provider.id}
          providerName={provider.name}
          subcategoryId={null}
          subcategoryName={null}
        />
      )}
    </>
  );
};

export default ProviderProfilePage;
